import {AxiosRequestConfig, AxiosResponse} from 'axios';
import {Affiliation} from '../backend/interfaces/affiliation';
import {AffiliationQuickAccess} from '../backend/interfaces/affiliation-quick-access';
import {Entity} from '../backend/interfaces/entity';
import {User} from '../backend/interfaces/user';
import {PatchAffiliation} from '../backend/requests/patch-affiliation';
import {PatchEntityContractAmendment} from '../backend/requests/patch-entity-contract-amendment';
import {PatchEntityLicenses} from '../backend/requests/patch-entity-licenses';
import {PatchEntitySettings} from '../backend/requests/patch-entity-settings';
import {PostEntity} from '../backend/requests/post-entity';
import {PutEntity} from '../backend/requests/put-entity';
import {EntityContractsOverview} from '../backend/responses/entity-contracts-overview';
import {EntityLicenses} from '../backend/responses/entity-licenses';
import {EntitySettings} from '../backend/responses/entity-settings';
import {UserAffiliation} from '../backend/responses/user-afiliation';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class EntitiesRepository {
  // Singleton Instance
  private static _instance: EntitiesRepository;

  // Private
  private logger: Logger;

  static get instance(): EntitiesRepository {
    if (!this._instance) {
      this._instance = new EntitiesRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('EntitiesRepository');
  }

  // Get an entity by id
  public async getById(entityId: string): Promise<Entity> {
    // Get entity from backend
    const response: AxiosResponse<Entity> = await BackendApiManager.instance.get<Entity>(
      `/entities/${entityId}`
    );

    // Return
    return response.data;
  }

  // Get all entities in the system
  public async get(
    skip: number,
    limit: number,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<Entity>}> {
    // Set url
    let url: string = `/entities?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get entities from backend
    const response: AxiosResponse<Array<Entity>> =
      await BackendApiManager.instance.get<Array<Entity>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Update an entity
  public async updateById(id: string, body: PutEntity): Promise<Entity> {
    // Update the entity
    const response: AxiosResponse<Entity> = await BackendApiManager.instance.put<Entity>(
      `/entities/${id}`,
      body
    );

    // Return
    return response.data;
  }

  // Create an entity
  public async create(body: PostEntity): Promise<Entity> {
    // Create the entity
    const response: AxiosResponse<Entity> = await BackendApiManager.instance.post<Entity>(
      '/entities',
      body
    );

    // Return
    return response.data;
  }

  // Delete an entity
  public async deleteById(id: string): Promise<void> {
    // Delete the entity
    await BackendApiManager.instance.delete<void>(`/entities/${id}`);
  }

  // Update the affiliation type of a user in an entity
  public async updateAffiliation(
    entityId: string,
    userId: string,
    body: PatchAffiliation
  ): Promise<Affiliation> {
    // Update the affiliation
    const response: AxiosResponse<Affiliation> =
      await BackendApiManager.instance.patch<Affiliation>(
        `/entities/${entityId}/users/${userId}`,
        body
      );

    // Return
    return response.data;
  }

  // Get the affiliation a user in an entity
  public async getAffiliation(entityId: string, userId: string): Promise<UserAffiliation> {
    // Get the affiliation
    const response: AxiosResponse<UserAffiliation> =
      await BackendApiManager.instance.get<UserAffiliation>(
        `/entities/${entityId}/users/${userId}`
      );

    // Return
    return response.data;
  }

  // Get the licenses of an entity
  public async getEntityLicenses(entityId: string): Promise<EntityLicenses> {
    // Get the number of licenses
    const response: AxiosResponse<EntityLicenses> =
      await BackendApiManager.instance.get<EntityLicenses>(`/entities/${entityId}/licenses`);

    // Return
    return response.data;
  }

  // Update the licenses of an entity
  public async updateEntityLicenses(
    entityId: string,
    body: PatchEntityLicenses
  ): Promise<EntityLicenses> {
    // Update the number of licenses
    const response: AxiosResponse<EntityLicenses> =
      await BackendApiManager.instance.patch<EntityLicenses>(
        `/entities/${entityId}/licenses`,
        body
      );

    // Return
    return response.data;
  }

  // Get all the settings of an entity
  public async getEntitySettings(entityId: string): Promise<EntitySettings> {
    const response: AxiosResponse<EntitySettings> =
      await BackendApiManager.instance.get<EntitySettings>(`/entities/${entityId}/settings`);

    return response.data;
  }

  // Update (only the provided) settings of an entity
  public async updateEntitySettings(
    entityId: string,
    body: PatchEntitySettings
  ): Promise<EntitySettings> {
    const response: AxiosResponse<EntitySettings> =
      await BackendApiManager.instance.patch<EntitySettings>(
        `/entities/${entityId}/settings`,
        body
      );

    return response.data;
  }

  public async getEntityContractsOverview(entityId: string): Promise<EntityContractsOverview> {
    const response = await BackendApiManager.instance.get<EntityContractsOverview>(
      `/entities/${entityId}/contracts/overview`
    );

    return response.data;
  }

  public async updateEntityContractsOverview(
    entityId: string,
    body: PatchEntityContractAmendment
  ): Promise<EntityContractsOverview> {
    const response = await BackendApiManager.instance.patch<EntityContractsOverview>(
      `/entities/${entityId}/contracts/overview`,
      body
    );

    return response.data;
  }

  public async updateQuickAccessCode(entityId: string, userId: string, code: string | undefined) {
    const response: AxiosResponse<AffiliationQuickAccess> =
      await BackendApiManager.instance.put<AffiliationQuickAccess>(
        `/entities/${entityId}/users/${userId}/quick-access`,
        {
          code,
        }
      );

    // Return
    return response.data;
  }

  public async removeQuickAccessCode(entityId: string, userId: string) {
    // Delete user
    await BackendApiManager.instance.delete<void>(
      `/entities/${entityId}/users/${userId}/quick-access`
    );
  }

  public async getBirtdays(
    entityId: string,
    startDate: string,
    endDate: string,
    config?: AxiosRequestConfig
  ): Promise<User[]> {
    const response = await BackendApiManager.instance.get<User[]>(
      `/entities/${entityId}/birthdays?startDate=${startDate}&endDate=${endDate}`,
      config
    );

    // Return
    return response.data;
  }
}
